import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import { useDepartments } from '@hooks/menu';
import {
  createMenuExtraction,
  processExtractionWithAI,
  selectIsExtracting,
} from '@store/menuExtractions';
import { selectEffectiveOutletId } from '@store/outlets';
import { CloudUpload, FileText, X } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { apiError, apiInfo } from '../../../util/notify';
import { AIExtractionLoadingModal } from './AIExtractionLoadingModal';
import { ExtractedTextPreviewModal } from './ExtractedTextPreviewModal';

interface MenuImportUploadProps {
  onExtractionComplete?: () => void;
}

const ACCEPTED_FILE_TYPES = [
  '.pdf',
  '.doc',
  '.docx',
  '.txt',
  '.xls',
  '.xlsx',
  '.csv',
  '.sql',
] as const;

type AcceptedFileExtension = (typeof ACCEPTED_FILE_TYPES)[number];

const FILE_TYPE_LABELS: Record<string, string> = {
  '.pdf': 'PDF Document',
  '.doc': 'Word Document',
  '.docx': 'Word Document',
  '.txt': 'Text File',
  '.xls': 'Excel Spreadsheet',
  '.xlsx': 'Excel Spreadsheet',
  '.csv': 'CSV Spreadsheet',
  '.sql': 'SQL Export',
};

const getFileExtension = (filename: string) =>
  filename.toLowerCase().slice(filename.lastIndexOf('.'));

const isValidFileType = (file: File) => {
  const ext = getFileExtension(file.name);
  return ACCEPTED_FILE_TYPES.includes(ext as AcceptedFileExtension);
};

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

export const MenuImportUpload: React.FC<MenuImportUploadProps> = ({
  onExtractionComplete,
}) => {
  const dispatch = useAppDispatch();
  const outletId = useAppSelector(selectEffectiveOutletId);
  const { data: allDepartments = [] } = useDepartments(outletId);
  const departments = allDepartments.filter((d) => d.status === 'active');
  const isExtracting = useAppSelector(selectIsExtracting);

  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [extractedText, setExtractedText] = useState('');
  const [isExtractingText, setIsExtractingText] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Extract text via Electron IPC (uses native Node modules: mammoth, xlsx)
  const extractTextFromFile = useCallback(
    async (file: File): Promise<string> => {
      const ext = getFileExtension(file.name);
      const arrayBuffer = await file.arrayBuffer();
      const base64Data = arrayBufferToBase64(arrayBuffer);

      const result = await window.electron.ipcRenderer.invoke(
        'extract-file-text',
        {
          buffer: base64Data,
          extension: ext,
          fileName: file.name,
        },
      );

      if (result && typeof result === 'object' && 'error' in result) {
        throw new Error(String(result.error));
      }

      if (typeof result === 'string') {
        return result;
      }

      throw new Error('Unexpected response from file extraction');
    },
    [],
  );

  const handleFileSelect = useCallback(
    async (file: File) => {
      if (!isValidFileType(file)) {
        apiError(
          `Invalid file type. Please upload: ${ACCEPTED_FILE_TYPES.join(', ')}`,
        );
        return;
      }

      setSelectedFile(file);
      setIsExtractingText(true);

      try {
        const text = await extractTextFromFile(file);
        setExtractedText(text);
        apiInfo(
          `Extracted ${text.length.toLocaleString()} characters from ${file.name}`,
        );
        setShowPreview(true);
      } catch (e) {
        apiError(
          e instanceof Error ? e.message : 'Failed to extract text from file',
        );
        setSelectedFile(null);
        setExtractedText('');
      } finally {
        setIsExtractingText(false);
      }
    },
    [extractTextFromFile],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect],
  );

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setExtractedText('');
    setShowPreview(false);
  }, []);

  const handleStartExtraction = useCallback(async () => {
    if (!outletId || !extractedText || !selectedFile) return;

    setShowPreview(false);

    try {
      const result = await dispatch(
        createMenuExtraction({
          outlet_id: outletId,
          raw_text: extractedText,
          file_name: selectedFile.name,
          file_type: getFileExtension(selectedFile.name),
        }),
      ).unwrap();

      await dispatch(
        processExtractionWithAI({
          extractionId: result.id,
          rawText: extractedText,
          departmentNames: departments.map((d) => d.name),
        }),
      ).unwrap();

      onExtractionComplete?.();
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Failed to extract menu');
    }
  }, [
    outletId,
    extractedText,
    selectedFile,
    departments,
    dispatch,
    onExtractionComplete,
  ]);

  return (
    <>
      <div className="space-y-6">
        {/* File Upload Zone */}
        <div
          className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
            isDragOver
              ? 'border-pos-primary bg-pos-light/10'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isExtractingText ? (
            <div className="py-8 flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-pos-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-gray-600 text-sm">Reading file...</p>
            </div>
          ) : selectedFile ? (
            <div className="py-4">
              <div className="inline-flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3">
                <FileText size={24} className="text-pos-primary" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">
                    {selectedFile.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {FILE_TYPE_LABELS[getFileExtension(selectedFile.name)]} •{' '}
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  className="ml-2 p-1 text-gray-400 hover:text-red-500 rounded"
                >
                  <X size={18} />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-3">
                {extractedText.length.toLocaleString()} characters extracted —{' '}
                <button
                  type="button"
                  onClick={() => setShowPreview(true)}
                  className="text-pos-primary underline hover:no-underline"
                >
                  preview content
                </button>
              </p>
            </div>
          ) : (
            <>
              <CloudUpload size={48} className="mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Upload Your Menu
              </h3>
              <p className="text-gray-500 mb-4">
                Drag and drop your menu file here, or click to browse
              </p>
              <p className="text-sm text-gray-400">
                Supported: PDF, DOC, DOCX, TXT, XLS, XLSX, CSV, SQL
              </p>
            </>
          )}

          <input
            type="file"
            accept={ACCEPTED_FILE_TYPES.join(',')}
            onChange={handleInputChange}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={isExtractingText || isExtracting}
          />
        </div>

        {departments.length === 0 && (
          <p className="text-sm text-amber-600 text-center">
            ⚠️ Please create at least one department before importing your menu.
          </p>
        )}
      </div>

      {/* Extracted text preview modal (explicit close only, no backdrop dismiss) */}
      <ExtractedTextPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        fileName={selectedFile?.name ?? ''}
        extractedText={extractedText}
        onProceed={handleStartExtraction}
        isProceedDisabled={departments.length === 0}
      />

      {/* AI processing loading modal — shown while edge function runs */}
      <AIExtractionLoadingModal
        isOpen={isExtracting}
        fileName={selectedFile?.name}
      />
    </>
  );
};
